import React, { useState, useEffect } from 'react';
import { getAvailableCarriers, getCourierDetails } from './cart/Utils';
import PricePayment from './placeorder/PricePayment';
import ItemsTable from './placeorder/ItemTable';
import AddressSection from './placeorder/AddressSecion';
import CarrierSelection from './placeorder/CarrierSelection';
import CarrierDetails from './placeorder/CarrierDetails';
import { upload_tracking_data_to_cloud } from './api/FirestoreUtils';

const PlaceOrder = ({ order, onConfirm, isExchange = false }) => {
  const address = order.shippingAddress || {};
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [carrierDetails, setCarrierDetails] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [trackUrls, setTrackUrls] = useState([]);
  const [shipmentIds, setShipmentIds] = useState([]);
  const [showOrderPlacedAnimation, setShowOrderPlacedAnimation] = useState(false);

  const handleCopy = async (value) => {
    const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
    await navigator.clipboard.writeText(str);
  };

  useEffect(() => {
    console.log("order", order)
    const fetchCarriers = async () => {
      setLoadingCarriers(true);
      setError('');
      try {
        const countryCode = address.country?.slice(0, 2).toUpperCase() || 'IN';
        const isInternational = countryCode !== 'IN';
        const response = await getAvailableCarriers(countryCode, isInternational);
        setCarriers(response || []);
      } catch (err) {
        setError('Failed to fetch carriers. Please try again.');
        console.error('Carrier fetch error:', err);
      } finally {
        setLoadingCarriers(false);
      }
    };
    fetchCarriers();
  }, [address.country]);

  useEffect(() => {
    const fetchCarrierDetails = async () => {
      if (!selectedCarrier) {
        setCarrierDetails([]);
        return;
      }
      setLoadingDetails(true);
      setError('');
      try {
        const countryCode = address.country?.slice(0, 2).toUpperCase() || 'IN';
        const details = await getCourierDetails(selectedCarrier, countryCode);
        setCarrierDetails(details || []);
      } catch (err) {
        setError('Failed to fetch carrier details. Please try again.');
        console.error('Carrier details error:', err);
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchCarrierDetails();
  }, [selectedCarrier, address.country]);

  const handleOrderPlaced = async (trackingInfos) => {
    // Log all tracking information before processing
    console.log('All Tracking Information in PlaceOrder:', trackingInfos);
    console.log('Appending tracking IDs for order:', order.id);
    console.log('User email:', order.userEmail);

    setTrackingNumbers(trackingInfos.map((info) => info.trackingNumber));
    setTrackUrls(trackingInfos.map((info) => info.trackUrl));
    setShipmentIds(trackingInfos.map((info) => info.shipmentId));
    setShowOrderPlacedAnimation(true);

    // Call onConfirm with all tracking information including carrier
    if (onConfirm) {
      try {
        // Upload each tracking info to Firestore
        for (const info of trackingInfos) {
          const result = await upload_tracking_data_to_cloud(
            order.id,
            order.userEmail, // Ensure this is the string email, not the order object
            info.trackingNumber,
            info.trackUrl,
            info.shipmentId,
            selectedCarrier
          );
          if (!result.success) {
            throw new Error(result.error);
          }
        }
        // Pass all tracking infos to onConfirm
        onConfirm({
          trackingInfos,
          carrier: selectedCarrier,
        });
      } catch (err) {
        console.error('Error uploading tracking data:', err);
        setError('Failed to upload tracking data. Please try again.');
      }
    }

    setTimeout(() => {
      setShowOrderPlacedAnimation(false);
      setSelectedCarrier('');
    }, 2000);
  };

  return (
    <div className="text-[#DBAE6E] max-h-[80vh] overflow-y-auto pr-2 relative">
      <h2 className="text-2xl font-bold mb-4">
        {isExchange ? 'Process Exchange Shipment' : 'Confirm Dispatch'}
      </h2>
      {showOrderPlacedAnimation && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75 z-20">
          <div className="text-center animate-bounce">
            <svg
              className="w-16 h-16 text-green-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-2xl text-white font-bold mt-4">
              {isExchange ? 'Exchange Processed!' : 'Order Dispatched!'}
            </p>
          </div>
        </div>
      )}
      <p className="p-2 text-sm text-gray-400">{order.id}</p>
      <PricePayment order={order} />
      <p className="text-sm text-gray-400 mb-2">
        <strong className="text-[#DBAE6E]">User:</strong>{' '}
        <span className="text-white">{order.userEmail}</span>
      </p>
      <ItemsTable items={order.items} />
      <AddressSection address={address} handleCopy={handleCopy} />
      {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}

      {trackingNumbers.length > 0 ? (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-[#DBAE6E] mb-4">Tracking Information</h3>
          {trackingNumbers.map((trackingNumber, index) => (
            <div key={index} className="bg-[#333] p-4 rounded-md shadow-md mb-4">
              <p className="text-sm text-gray-200">
                <strong className="text-[#DBAE6E]">Carrier:</strong> {selectedCarrier}
              </p>
              <p className="text-sm text-gray-200">
                <strong className="text-[#DBAE6E]">Tracking Number:</strong> {trackingNumber}
                <button
                  onClick={() => handleCopy(trackingNumber)}
                  className="ml-2 text-[#DBAE6E] hover:text-[#E8C78F]"
                  title="Copy Tracking Number"
                >
                  <svg
                    className="w-4 h-4 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </p>
              <p className="text-sm text-gray-200">
                <strong className="text-[#DBAE6E]">Tracking URL:</strong>{' '}
                <a
                  href={trackUrls[index]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#DBAE6E] hover:underline"
                >
                  {trackUrls[index]}
                </a>
                <button
                  onClick={() => handleCopy(trackUrls[index])}
                  className="ml-2 text-[#DBAE6E] hover:text-[#E8C78F]"
                  title="Copy Tracking URL"
                >
                  <svg
                    className="w-4 h-4 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </p>
              <p className="text-sm text-gray-200">
                <strong className="text-[#DBAE6E]">Shipment ID:</strong> {shipmentIds[index]}
                <button
                  onClick={() => handleCopy(shipmentIds[index])}
                  className="ml-2 text-[#DBAE6E] hover:text-[#E8C78F]"
                  title="Copy Shipment ID"
                >
                  <svg
                    className="w-4 h-4 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </p>
              <p className="text-sm text-green-500 mt-2">
                <strong>Status:</strong> {isExchange ? 'Exchange Processed' : 'Dispatched'}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <>
          <CarrierSelection
            carriers={carriers}
            selectedCarrier={selectedCarrier}
            setSelectedCarrier={setSelectedCarrier}
            loadingCarriers={loadingCarriers}
          />
          <CarrierDetails
            selectedCarrier={selectedCarrier}
            orderDetails={order}
            carrierDetails={carrierDetails}
            loadingDetails={loadingDetails}
            onOrderPlaced={handleOrderPlaced}
            isExchange={isExchange}
          />
        </>
      )}
    </div>
  );
};

export default PlaceOrder;